import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { Change, ChangeState } from '../../models/file-vault.models';

export interface DashboardClickEvent {
  type: 'state' | 'overdue' | 'assigned' | 'raised' | 'change';
  state?: ChangeState;
  change?: Change;
}

@Component({
  selector: 'fv-change-dashboard',
  standalone: true,
  template: `
    <div class="dashboard-container">
      <div class="dashboard-header" *ngIf="showHeader">
        <h2>Change Management</h2>
        <button kendoButton [themeColor]="'primary'" (click)="onCreateClick()">
          New Change
        </button>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid">
        <div class="stat-card" (click)="onStatClick('state', ChangeState.Draft)">
          <div class="stat-icon draft">📝</div>
          <div class="stat-content">
            <span class="stat-value">{{ stateCounts[ChangeState.Draft] || 0 }}</span>
            <span class="stat-label">Draft</span>
          </div>
        </div>

        <div class="stat-card" (click)="onStatClick('state', ChangeState.Submitted)">
          <div class="stat-icon submitted">📤</div>
          <div class="stat-content">
            <span class="stat-value">{{ stateCounts[ChangeState.Submitted] || 0 }}</span>
            <span class="stat-label">Submitted</span>
          </div>
        </div>

        <div class="stat-card" (click)="onStatClick('state', ChangeState.InReview)">
          <div class="stat-icon in-review">👀</div>
          <div class="stat-content">
            <span class="stat-value">{{ stateCounts[ChangeState.InReview] || 0 }}</span>
            <span class="stat-label">In Review</span>
          </div>
        </div>

        <div class="stat-card" (click)="onStatClick('state', ChangeState.Approved)">
          <div class="stat-icon approved">✅</div>
          <div class="stat-content">
            <span class="stat-value">{{ stateCounts[ChangeState.Approved] || 0 }}</span>
            <span class="stat-label">Approved</span>
          </div>
        </div>

        <div class="stat-card" (click)="onStatClick('state', ChangeState.InImplementation)">
          <div class="stat-icon implementing">🔧</div>
          <div class="stat-content">
            <span class="stat-value">{{ stateCounts[ChangeState.InImplementation] || 0 }}</span>
            <span class="stat-label">Implementing</span>
          </div>
        </div>

        <div class="stat-card overdue" (click)="onStatClick('overdue')">
          <div class="stat-icon overdue">⚠️</div>
          <div class="stat-content">
            <span class="stat-value">{{ overdueChanges.length }}</span>
            <span class="stat-label">Overdue</span>
          </div>
        </div>
      </div>

      <!-- Quick Lists -->
      <div class="lists-grid">
        <!-- Overdue Changes -->
        <div class="list-card" *ngIf="overdueChanges.length > 0">
          <div class="list-header">
            <h3>Overdue Changes</h3>
            <button kendoButton [look]="'flat'" (click)="onStatClick('overdue')">View All</button>
          </div>
          <div class="list-content">
            <div class="list-item" *ngFor="let change of overdueChanges.slice(0, 5)"
                 (click)="onChangeClick(change)">
              <div class="item-main">
                <span class="item-number">{{ change.changeNumber }}</span>
                <span class="item-title">{{ change.title }}</span>
              </div>
              <div class="item-meta">
                <span class="overdue-days">{{ getDaysOverdue(change) }} days overdue</span>
              </div>
            </div>
          </div>
        </div>

        <!-- My Assigned Changes -->
        <div class="list-card" *ngIf="showMyAssigned">
          <div class="list-header">
            <h3>My Assigned</h3>
            <button kendoButton [look]="'flat'" (click)="onStatClick('assigned')">View All</button>
          </div>
          <div class="list-content">
            <div class="list-item" *ngFor="let change of myAssignedChanges.slice(0, 5)"
                 (click)="onChangeClick(change)">
              <div class="item-main">
                <span class="item-number">{{ change.changeNumber }}</span>
                <span class="item-title">{{ change.title }}</span>
              </div>
              <div class="item-meta">
                <span class="state-badge" [ngClass]="getStateBadgeClass(change.state)">
                  {{ getStateLabel(change.state) }}
                </span>
              </div>
            </div>
            <div class="empty-list" *ngIf="myAssignedChanges.length === 0">
              No assigned changes
            </div>
          </div>
        </div>

        <!-- My Raised Changes -->
        <div class="list-card" *ngIf="showMyRaised">
          <div class="list-header">
            <h3>My Raised</h3>
            <button kendoButton [look]="'flat'" (click)="onStatClick('raised')">View All</button>
          </div>
          <div class="list-content">
            <div class="list-item" *ngFor="let change of myRaisedChanges.slice(0, 5)"
                 (click)="onChangeClick(change)">
              <div class="item-main">
                <span class="item-number">{{ change.changeNumber }}</span>
                <span class="item-title">{{ change.title }}</span>
              </div>
              <div class="item-meta">
                <span class="state-badge" [ngClass]="getStateBadgeClass(change.state)">
                  {{ getStateLabel(change.state) }}
                </span>
              </div>
            </div>
            <div class="empty-list" *ngIf="myRaisedChanges.length === 0">
              No changes raised
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div class="loading-overlay" *ngIf="isLoading">
        <span>Loading dashboard...</span>
      </div>
    </div>
  `,
  styles: [`
    .dashboard-container {
      padding: 16px;
      position: relative;
    }

    .dashboard-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    .dashboard-header h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 500;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 16px;
      margin-bottom: 24px;
    }

    .stat-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      background: var(--fv-surface);
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .stat-card:hover {
      border-color: var(--fv-text-accent);
      box-shadow: 0 2px 8px var(--fv-shadow);
    }

    .stat-card.overdue {
      border-color: var(--fv-status-error-border);
      background: var(--fv-status-error-bg);
    }

    .stat-card.overdue:hover {
      border-color: var(--fv-status-error-fg);
    }

    .stat-icon {
      font-size: 24px;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
    }

    .stat-icon.draft { background: var(--fv-status-draft-bg); }
    .stat-icon.submitted { background: var(--fv-status-info-bg); }
    .stat-icon.in-review { background: var(--fv-status-warning-bg); }
    .stat-icon.approved { background: var(--fv-status-success-bg); }
    .stat-icon.implementing { background: var(--fv-status-indigo-bg); }
    .stat-icon.overdue { background: var(--fv-status-error-bg); }

    .stat-content {
      display: flex;
      flex-direction: column;
    }

    .stat-value {
      font-size: 24px;
      font-weight: 600;
      line-height: 1;
    }

    .stat-label {
      font-size: 12px;
      color: var(--fv-text-secondary);
      margin-top: 4px;
    }

    .lists-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 16px;
    }

    .list-card {
      background: var(--fv-surface);
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      overflow: hidden;
    }

    .list-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      background: var(--fv-surface-alt);
      border-bottom: 1px solid var(--fv-border);
    }

    .list-header h3 {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
    }

    .list-content {
      max-height: 280px;
      overflow-y: auto;
    }

    .list-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid var(--fv-border-subtle);
      cursor: pointer;
      transition: background 0.2s ease;
    }

    .list-item:hover {
      background: var(--fv-surface-hover);
    }

    .list-item:last-child {
      border-bottom: none;
    }

    .item-main {
      display: flex;
      flex-direction: column;
      gap: 4px;
      overflow: hidden;
    }

    .item-number {
      font-family: monospace;
      font-size: 12px;
      font-weight: 600;
      color: var(--fv-text-accent);
    }

    .item-title {
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .item-meta {
      flex-shrink: 0;
      margin-left: 12px;
    }

    .overdue-days {
      font-size: 11px;
      color: var(--fv-status-error-fg);
      font-weight: 500;
    }

    .state-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 500;
    }

    .state-badge.draft { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .state-badge.submitted { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .state-badge.in-review { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .state-badge.approved { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .state-badge.rejected { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .state-badge.in-implementation { background: var(--fv-status-indigo-bg); color: var(--fv-status-indigo-fg); }
    .state-badge.implemented { background: var(--fv-status-teal-bg); color: var(--fv-status-teal-fg); }
    .state-badge.closed { background: var(--fv-status-bluegrey-bg); color: var(--fv-status-bluegrey-fg); }

    .empty-list {
      padding: 24px;
      text-align: center;
      color: var(--fv-text-muted);
      font-size: 13px;
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--fv-surface-overlay);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: var(--fv-text-secondary);
    }
  `],
  imports: [CommonModule, ButtonsModule]
})
export class ChangeDashboardComponent implements OnInit {
  private api = inject(FileVaultApiService);

  /** Current user ID (for my assigned) */
  @Input() currentUserId?: number;

  /** Current username (for my raised) */
  @Input() currentUserName?: string;

  /** Show dashboard header */
  @Input() showHeader = true;

  /** Show my assigned section */
  @Input() showMyAssigned = true;

  /** Show my raised section */
  @Input() showMyRaised = true;

  /** Emitted when create is clicked */
  @Output() createClick = new EventEmitter<void>();

  /** Emitted when a dashboard item is clicked */
  @Output() itemClick = new EventEmitter<DashboardClickEvent>();

  ChangeState = ChangeState;

  stateCounts: Record<number, number> = {};
  overdueChanges: Change[] = [];
  myAssignedChanges: Change[] = [];
  myRaisedChanges: Change[] = [];
  isLoading = false;

  ngOnInit(): void {
    this.loadDashboard();
  }

  async loadDashboard(): Promise<void> {
    this.isLoading = true;

    try {
      const promises: Promise<any>[] = [
        this.api.getChangeCountByState().toPromise(),
        this.api.getOverdueChanges().toPromise()
      ];

      if (this.showMyAssigned && this.currentUserId) {
        promises.push(this.api.getMyAssignedChanges(this.currentUserId).toPromise());
      }

      if (this.showMyRaised && this.currentUserName) {
        promises.push(this.api.getMyRaisedChanges(this.currentUserName).toPromise());
      }

      const results = await Promise.all(promises);

      // Parse state counts
      const counts = results[0] as Record<string, number>;
      this.stateCounts = {};
      if (counts) {
        Object.entries(counts).forEach(([key, value]) => {
          // Handle both string keys (e.g., "Draft") and numeric keys
          const stateValue = this.parseStateKey(key);
          if (stateValue !== null) {
            this.stateCounts[stateValue] = value;
          }
        });
      }

      this.overdueChanges = results[1] ?? [];

      let resultIndex = 2;
      if (this.showMyAssigned && this.currentUserId) {
        this.myAssignedChanges = results[resultIndex++] ?? [];
      }

      if (this.showMyRaised && this.currentUserName) {
        this.myRaisedChanges = results[resultIndex++] ?? [];
      }
    } catch (error) {
      console.error('Failed to load dashboard', error);
    } finally {
      this.isLoading = false;
    }
  }

  private parseStateKey(key: string): ChangeState | null {
    // Try as number first
    const numValue = parseInt(key, 10);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 8) {
      return numValue as ChangeState;
    }

    // Try as string name
    const stateMap: Record<string, ChangeState> = {
      'Draft': ChangeState.Draft,
      'Submitted': ChangeState.Submitted,
      'InReview': ChangeState.InReview,
      'Approved': ChangeState.Approved,
      'Rejected': ChangeState.Rejected,
      'InImplementation': ChangeState.InImplementation,
      'Implemented': ChangeState.Implemented,
      'Closed': ChangeState.Closed,
      'Cancelled': ChangeState.Cancelled
    };

    return stateMap[key] ?? null;
  }

  onCreateClick(): void {
    this.createClick.emit();
  }

  onStatClick(type: 'state' | 'overdue' | 'assigned' | 'raised', state?: ChangeState): void {
    this.itemClick.emit({ type, state });
  }

  onChangeClick(change: Change): void {
    this.itemClick.emit({ type: 'change', change });
  }

  getDaysOverdue(change: Change): number {
    if (!change.dueDate) return 0;
    const dueDate = new Date(change.dueDate);
    const now = new Date();
    const diffTime = now.getTime() - dueDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  getStateLabel(state: ChangeState): string {
    switch (state) {
      case ChangeState.Draft: return 'Draft';
      case ChangeState.Submitted: return 'Submitted';
      case ChangeState.InReview: return 'In Review';
      case ChangeState.Approved: return 'Approved';
      case ChangeState.Rejected: return 'Rejected';
      case ChangeState.InImplementation: return 'Implementing';
      case ChangeState.Implemented: return 'Implemented';
      case ChangeState.Closed: return 'Closed';
      case ChangeState.Cancelled: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  getStateBadgeClass(state: ChangeState): string {
    switch (state) {
      case ChangeState.Draft: return 'draft';
      case ChangeState.Submitted: return 'submitted';
      case ChangeState.InReview: return 'in-review';
      case ChangeState.Approved: return 'approved';
      case ChangeState.Rejected: return 'rejected';
      case ChangeState.InImplementation: return 'in-implementation';
      case ChangeState.Implemented: return 'implemented';
      case ChangeState.Closed: return 'closed';
      case ChangeState.Cancelled: return 'cancelled';
      default: return '';
    }
  }

  refresh(): void {
    this.loadDashboard();
  }
}
